import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { Trash2, Pencil } from 'lucide-react'

interface ChapterOption { id: number; name: string }
interface Section {
  id: number
  name: string
  slug: string | null
  sort_order: number
  chapter_id: number
  chapter_name: string
  subject_name: string
}
interface Props {
  sections: Section[]
  chapters: ChapterOption[]
}

export default function SectionsIndex({ sections, chapters }: Props) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [sortOrder, setSortOrder] = useState(0)
  const [chapterId, setChapterId] = useState<number | ''>(chapters[0]?.id ?? '')

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editSortOrder, setEditSortOrder] = useState(0)
  const [editChapterId, setEditChapterId] = useState<number | ''>('')

  const handleCreate = () => {
    if (!chapterId) return
    router.post('/admin/sections', {
      section: {
        name,
        slug: slug || null,
        sort_order: sortOrder,
        chapter_id: chapterId
      }
    }, {
      onSuccess: () => {
        setName('')
        setSlug('')
        setSortOrder(0)
      }
    })
  }

  const startEdit = (s: Section) => {
    setEditingId(s.id)
    setEditName(s.name)
    setEditSlug(s.slug ?? '')
    setEditSortOrder(s.sort_order)
    setEditChapterId(s.chapter_id)
  }

  const handleUpdate = (id: number) => {
    router.patch(`/admin/sections/${id}`, {
      section: {
        name: editName,
        slug: editSlug || null,
        sort_order: editSortOrder,
        chapter_id: editChapterId
      }
    }, { onSuccess: () => setEditingId(null) })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure?')) return
    router.delete(`/admin/sections/${id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Taxonomy Management</h1>
          <p className="text-muted-foreground mt-1">Manage standards, subjects, chapters, sections, exams, and syllabi</p>
        </div>

        {/* Create form */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Add Section</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm md:col-span-2"
                value={chapterId}
                onChange={(e) => setChapterId(Number(e.target.value))}
              >
                <option value="" disabled>Select Chapter *</option>
                {chapters.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <Input
                placeholder="Section Name (e.g. Projectile Motion)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <Input
                type="number"
                placeholder="Sort order"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={!name.trim() || !chapterId}>
                Add Section
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Sections ({sections.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Chapter</TableHead>
                  <TableHead>Section Name</TableHead>
                  <TableHead className="w-24">Sort Order</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No sections created yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  sections.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-muted-foreground">{s.subject_name}</TableCell>
                      <TableCell>
                        {editingId === s.id ? (
                          <select
                            className="flex h-9 rounded-md border border-input bg-background px-2 py-1 text-sm"
                            value={editChapterId}
                            onChange={(e) => setEditChapterId(Number(e.target.value))}
                          >
                            {chapters.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        ) : s.chapter_name}
                      </TableCell>
                      <TableCell className="font-medium">
                        {editingId === s.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        ) : s.name}
                      </TableCell>
                      <TableCell>
                        {editingId === s.id ? (
                          <Input
                            type="number"
                            value={editSortOrder}
                            onChange={(e) => setEditSortOrder(Number(e.target.value))}
                            className="w-20"
                          />
                        ) : s.sort_order}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === s.id ? (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={() => handleUpdate(s.id)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => startEdit(s)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(s.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
